export interface DocumentTypeField {
  name: string
  description: string
}

export interface DocumentType {
  slug: string
  name: string
  plural: string
  description: string
  heroDescription: string
  fields: DocumentTypeField[]
  useCases: string[]
  relatedSolutionSlug: string
  relatedCaseStudySlugs: string[]
  relatedBlogSlugs: string[]
}

export const documentTypes: DocumentType[] = [
  {
    slug: 'invoices',
    name: 'Invoice Data Extraction',
    plural: 'Invoices',
    description:
      'Automatically extract invoice numbers, line items, totals, and payment terms from supplier invoices in any format.',
    heroDescription:
      'Invoices arrive in dozens of formats from hundreds of suppliers -- yet every one needs the same structured data keyed into your ERP. DocumentIQ reads each invoice, extracts every field you configure, and delivers clean, validated data ready for import. Stop re-keying and start scaling your accounts payable workflow.',
    fields: [
      { name: 'invoice_number', description: 'Unique identifier assigned by the vendor to the invoice.' },
      { name: 'date', description: 'Date the invoice was issued.' },
      { name: 'vendor_name', description: 'Name of the company or individual issuing the invoice.' },
      { name: 'line_items', description: 'Itemized list of goods or services with descriptions, quantities, and unit prices.' },
      { name: 'subtotal', description: 'Sum of all line items before tax and adjustments.' },
      { name: 'tax', description: 'Total tax amount applied to the invoice.' },
      { name: 'total', description: 'Final amount due including tax and any adjustments.' },
      { name: 'payment_terms', description: 'Payment deadline or conditions such as Net 30, Net 60, or due on receipt.' },
      { name: 'po_reference', description: 'Purchase order number referenced on the invoice for cross-matching.' },
    ],
    useCases: [
      'Automate accounts payable data entry across multiple vendor formats',
      'Three-way matching between invoices, purchase orders, and receipts',
      'Audit trail generation for tax compliance and financial reporting',
      'Bulk invoice processing for month-end close acceleration',
    ],
    relatedSolutionSlug: 'manufacturing',
    relatedCaseStudySlugs: ['invoice-digitization'],
    relatedBlogSlugs: ['how-to-extract-data-from-pdf-invoices-using-ai'],
  },
  {
    slug: 'contracts',
    name: 'Contract Data Extraction',
    plural: 'Contracts',
    description:
      'Extract parties, dates, clauses, and obligations from legal contracts to build a searchable contract repository.',
    heroDescription:
      'Legal and procurement teams manage hundreds of contracts with critical dates, renewal windows, and liability terms buried in dense legalese. DocumentIQ parses each contract, extracts the clauses and terms you define, and surfaces them in structured tables you can search, filter, and export. Never miss a renewal deadline or hidden escalation clause again.',
    fields: [
      { name: 'parties', description: 'Names and roles of all parties involved in the contract.' },
      { name: 'effective_date', description: 'Date the contract becomes legally binding.' },
      { name: 'term_length', description: 'Duration of the contract, e.g. 12 months, 3 years.' },
      { name: 'renewal_date', description: 'Date on which the contract auto-renews or requires action.' },
      { name: 'escalation_clause', description: 'Terms governing price increases tied to CPI, inflation, or fixed percentages.' },
      { name: 'liability_cap', description: 'Maximum liability amount or limitation of liability language.' },
      { name: 'termination_conditions', description: 'Conditions under which either party may terminate the agreement.' },
      { name: 'governing_law', description: 'Jurisdiction and legal framework governing the contract.' },
    ],
    useCases: [
      'Build a searchable repository of contract terms across your entire portfolio',
      'Proactively identify upcoming renewals and price escalation triggers',
      'Compare liability caps and termination conditions across vendor agreements',
      'Accelerate due diligence during M&A by extracting key terms at scale',
    ],
    relatedSolutionSlug: 'professional-services',
    relatedCaseStudySlugs: ['contract-price-escalation'],
    relatedBlogSlugs: ['hidden-cost-of-missing-price-escalation-clauses'],
  },
  {
    slug: 'purchase-orders',
    name: 'Purchase Order Extraction',
    plural: 'Purchase Orders',
    description:
      'Extract PO numbers, line items, delivery dates, and shipping terms from purchase orders for automated procurement.',
    heroDescription:
      'Purchase orders are the backbone of procurement, but manually keying PO details into your systems is slow and error-prone. DocumentIQ extracts every field from your purchase orders -- line items, delivery schedules, shipping terms -- and structures them for direct import into your ERP or procurement platform.',
    fields: [
      { name: 'po_number', description: 'Unique purchase order identifier assigned by the buyer.' },
      { name: 'date', description: 'Date the purchase order was issued.' },
      { name: 'buyer', description: 'Name and details of the purchasing organization.' },
      { name: 'supplier', description: 'Name and details of the supplying organization.' },
      { name: 'line_items', description: 'Itemized list of goods or services ordered with quantities and unit prices.' },
      { name: 'delivery_date', description: 'Expected or required delivery date for the order.' },
      { name: 'shipping_terms', description: 'Shipping method, carrier preference, and delivery instructions.' },
      { name: 'total_amount', description: 'Total value of the purchase order.' },
    ],
    useCases: [
      'Automate PO-to-invoice matching for accounts payable',
      'Track delivery commitments across suppliers',
      'Feed structured PO data into ERP and MRP systems',
      'Monitor procurement spend by supplier, category, and period',
    ],
    relatedSolutionSlug: 'manufacturing',
    relatedCaseStudySlugs: [],
    relatedBlogSlugs: ['5-ways-ai-document-extraction-reduces-procurement-costs'],
  },
  {
    slug: 'bill-of-lading',
    name: 'Bill of Lading Extraction',
    plural: 'Bills of Lading',
    description:
      'Extract shipper, consignee, origin, destination, and freight details from bills of lading for logistics automation.',
    heroDescription:
      'Bills of lading are the most critical document in international shipping -- and every one is formatted differently. DocumentIQ extracts shipper and consignee details, origin and destination ports, commodity descriptions, and freight charges from any B/L format. Reduce manual data entry in your TMS and accelerate cargo clearance.',
    fields: [
      { name: 'shipper', description: 'Name and address of the party shipping the goods.' },
      { name: 'consignee', description: 'Name and address of the party receiving the goods.' },
      { name: 'origin', description: 'Port or location where the shipment originates.' },
      { name: 'destination', description: 'Port or location where the shipment is delivered.' },
      { name: 'weight', description: 'Total weight of the shipment, including unit of measure.' },
      { name: 'piece_count', description: 'Number of packages, containers, or units in the shipment.' },
      { name: 'commodity_description', description: 'Description of the goods being shipped.' },
      { name: 'carrier', description: 'Name of the carrier or shipping line transporting the goods.' },
      { name: 'freight_charges', description: 'Total freight cost and payment terms (prepaid or collect).' },
    ],
    useCases: [
      'Automate data entry into transportation management systems',
      'Cross-reference B/L data with customs declarations for compliance',
      'Track shipments across carriers and routes',
      'Accelerate cargo release by pre-populating clearance forms',
    ],
    relatedSolutionSlug: 'logistics',
    relatedCaseStudySlugs: ['shipping-document-processing'],
    relatedBlogSlugs: ['automating-bill-of-lading-processing-with-ai'],
  },
  {
    slug: 'packing-lists',
    name: 'Packing List Extraction',
    plural: 'Packing Lists',
    description:
      'Extract package counts, weights, dimensions, and contents descriptions from packing lists for warehouse operations.',
    heroDescription:
      'Packing lists accompany every shipment but rarely share a format. DocumentIQ extracts package counts, weights, dimensions, and contents descriptions from any packing list layout. Feed structured data directly into your WMS to speed up receiving, reduce discrepancies, and eliminate manual tallying on the dock.',
    fields: [
      { name: 'shipper', description: 'Name and address of the party who packed and shipped the goods.' },
      { name: 'consignee', description: 'Name and address of the receiving party.' },
      { name: 'package_count', description: 'Total number of packages, cartons, or pallets in the shipment.' },
      { name: 'gross_weight', description: 'Total weight of the shipment including packaging.' },
      { name: 'net_weight', description: 'Weight of the goods excluding packaging materials.' },
      { name: 'dimensions', description: 'Physical dimensions of each package (length, width, height).' },
      { name: 'contents_description', description: 'Description of items contained in each package.' },
      { name: 'marks_and_numbers', description: 'Shipping marks, reference numbers, or identifiers on each package.' },
    ],
    useCases: [
      'Speed up warehouse receiving by pre-populating WMS with expected contents',
      'Verify shipment completeness against purchase orders and bills of lading',
      'Generate discrepancy reports when actual contents differ from packing list',
      'Automate customs documentation preparation from packing list data',
    ],
    relatedSolutionSlug: 'logistics',
    relatedCaseStudySlugs: ['warehouse-receiving-automation'],
    relatedBlogSlugs: [],
  },
  {
    slug: 'certificates-of-origin',
    name: 'Certificate of Origin Extraction',
    plural: 'Certificates of Origin',
    description:
      'Extract exporter details, HS codes, and certifying authority from certificates of origin for trade compliance.',
    heroDescription:
      'Certificates of origin determine tariff rates, trade agreement eligibility, and customs clearance timelines. DocumentIQ extracts exporter and consignee details, HS codes, country of origin, and certifying authority information from any certificate format. Ensure compliance and accelerate duty calculations across your import operations.',
    fields: [
      { name: 'exporter', description: 'Name and address of the exporting party.' },
      { name: 'consignee', description: 'Name and address of the importing party.' },
      { name: 'country_of_origin', description: 'Country where the goods were manufactured or produced.' },
      { name: 'hs_codes', description: 'Harmonized System codes classifying the goods for customs purposes.' },
      { name: 'description_of_goods', description: 'Detailed description of the goods covered by the certificate.' },
      { name: 'certifying_authority', description: 'Chamber of commerce or authority that certified the origin.' },
    ],
    useCases: [
      'Automate preferential tariff determination under free trade agreements',
      'Build a compliance database of origin declarations by supplier',
      'Cross-reference HS codes with customs declarations for audit readiness',
      'Reduce manual review time for import clearance documentation',
    ],
    relatedSolutionSlug: 'logistics',
    relatedCaseStudySlugs: ['customs-declaration-extraction'],
    relatedBlogSlugs: [],
  },
  {
    slug: 'mill-test-certificates',
    name: 'Mill Test Certificate Extraction',
    plural: 'Mill Test Certificates',
    description:
      'Extract heat numbers, chemical composition, and mechanical properties from mill test certificates for quality assurance.',
    heroDescription:
      'Mill test certificates verify that raw materials meet specification before they enter your production line. DocumentIQ extracts heat numbers, chemical composition, yield and tensile strength, elongation, and hardness values from any MTC format. Automate quality verification and build a traceable materials database.',
    fields: [
      { name: 'heat_number', description: 'Unique identifier for the production batch or heat of material.' },
      { name: 'grade', description: 'Material grade or specification designation.' },
      { name: 'chemical_composition', description: 'Percentage breakdown of chemical elements in the material.' },
      { name: 'yield_strength', description: 'Stress at which the material begins to deform plastically.' },
      { name: 'tensile_strength', description: 'Maximum stress the material can withstand before breaking.' },
      { name: 'elongation', description: 'Percentage of length increase at fracture, indicating ductility.' },
      { name: 'hardness', description: 'Material hardness value and test method (e.g. Rockwell, Brinell).' },
      { name: 'standard', description: 'Industry standard the material was tested against (e.g. ASTM A36, EN 10204).' },
    ],
    useCases: [
      'Automate incoming material quality verification against specifications',
      'Build a searchable materials traceability database by heat number',
      'Flag out-of-spec materials before they enter production',
      'Generate compliance reports for ISO 9001 and industry audits',
    ],
    relatedSolutionSlug: 'manufacturing',
    relatedCaseStudySlugs: ['quality-certificate-compliance'],
    relatedBlogSlugs: [],
  },
  {
    slug: 'spec-sheets',
    name: 'Specification Sheet Extraction',
    plural: 'Specification Sheets',
    description:
      'Extract part numbers, materials, dimensions, tolerances, and costs from spec sheets for engineering and procurement.',
    heroDescription:
      'Specification sheets define every detail of a part or component -- materials, dimensions, tolerances, and costs. DocumentIQ extracts these fields from any spec sheet format, turning unstructured PDFs into structured BOM data. Feed the results directly into your PLM or ERP system and eliminate manual transcription errors.',
    fields: [
      { name: 'part_number', description: 'Unique identifier for the part or component.' },
      { name: 'material', description: 'Material type or composition specified for the part.' },
      { name: 'dimensions', description: 'Physical dimensions including length, width, height, and diameter.' },
      { name: 'tolerances', description: 'Acceptable deviation ranges for each dimension.' },
      { name: 'surface_finish', description: 'Required surface finish or treatment specification.' },
      { name: 'quantity_per_assembly', description: 'Number of this part required per assembly or unit.' },
      { name: 'unit_cost', description: 'Cost per unit of the part or component.' },
    ],
    useCases: [
      'Digitize supplier spec sheets into structured BOM data',
      'Compare specifications across alternative suppliers',
      'Feed part data directly into PLM and ERP systems',
      'Identify cost-saving opportunities through spec sheet analysis at scale',
    ],
    relatedSolutionSlug: 'manufacturing',
    relatedCaseStudySlugs: ['bill-of-materials-extraction'],
    relatedBlogSlugs: [],
  },
  {
    slug: 'customs-declarations',
    name: 'Customs Declaration Extraction',
    plural: 'Customs Declarations',
    description:
      'Extract HS codes, declared values, incoterms, and duty rates from customs declarations for trade compliance automation.',
    heroDescription:
      'Customs declarations are dense, highly structured documents that vary by country and trade corridor. DocumentIQ extracts declarant details, HS codes, declared values, incoterms, and duty rates from any customs form. Automate compliance checks, duty calculations, and broker communication across your entire import portfolio.',
    fields: [
      { name: 'declarant', description: 'Name and identification of the party filing the customs declaration.' },
      { name: 'hs_code', description: 'Harmonized System code classifying the imported goods.' },
      { name: 'country_of_origin', description: 'Country where the goods were manufactured or produced.' },
      { name: 'declared_value', description: 'Total declared customs value of the goods.' },
      { name: 'quantity', description: 'Number of units or items declared.' },
      { name: 'weight', description: 'Gross and net weight of the declared goods.' },
      { name: 'incoterms', description: 'International commercial terms defining delivery obligations (e.g. FOB, CIF, DDP).' },
      { name: 'duty_rate', description: 'Applicable duty or tariff rate for the declared goods.' },
    ],
    useCases: [
      'Automate duty and tariff calculations across import shipments',
      'Cross-reference declared values with commercial invoices for audit compliance',
      'Track HS code usage and origin declarations by supplier',
      'Accelerate customs broker communication with pre-extracted declaration data',
    ],
    relatedSolutionSlug: 'logistics',
    relatedCaseStudySlugs: ['customs-declaration-extraction'],
    relatedBlogSlugs: [],
  },
  {
    slug: 'receipts',
    name: 'Receipt Data Extraction',
    plural: 'Receipts',
    description:
      'Extract merchant names, items, totals, and payment methods from receipts for expense management and reconciliation.',
    heroDescription:
      'Receipts come in every shape, size, and format -- thermal prints, PDFs, digital downloads. DocumentIQ extracts merchant details, itemized purchases, totals, tax amounts, and payment methods from any receipt format. Automate expense reporting, streamline reconciliation, and eliminate the shoebox of paper receipts.',
    fields: [
      { name: 'merchant_name', description: 'Name of the business or store that issued the receipt.' },
      { name: 'date', description: 'Date and time of the transaction.' },
      { name: 'items', description: 'Itemized list of purchased goods or services with prices.' },
      { name: 'subtotal', description: 'Sum of all items before tax.' },
      { name: 'tax', description: 'Tax amount charged on the transaction.' },
      { name: 'total', description: 'Final amount paid including tax.' },
      { name: 'payment_method', description: 'Method of payment used (e.g. credit card, cash, digital wallet).' },
    ],
    useCases: [
      'Automate employee expense report data entry',
      'Reconcile credit card statements with individual receipts',
      'Categorize spending by merchant, category, and time period',
      'Generate tax-ready expense summaries for reimbursement',
    ],
    relatedSolutionSlug: 'manufacturing',
    relatedCaseStudySlugs: [],
    relatedBlogSlugs: [],
  },
  {
    slug: 'financial-statements',
    name: 'Financial Statement Extraction',
    plural: 'Financial Statements',
    description:
      'Extract revenue, expenses, assets, liabilities, and equity from financial statements for analysis and reporting.',
    heroDescription:
      'Financial statements contain the numbers that drive investment decisions, credit assessments, and regulatory filings. DocumentIQ extracts revenue, COGS, operating expenses, net income, and balance sheet figures from income statements and balance sheets in any format. Build structured financial datasets for analysis, benchmarking, and compliance.',
    fields: [
      { name: 'revenue', description: 'Total revenue or net sales for the reporting period.' },
      { name: 'cogs', description: 'Cost of goods sold or cost of revenue.' },
      { name: 'gross_profit', description: 'Revenue minus cost of goods sold.' },
      { name: 'operating_expenses', description: 'Total operating expenses including SGA, R&D, and depreciation.' },
      { name: 'net_income', description: 'Bottom-line profit after all expenses, interest, and taxes.' },
      { name: 'total_assets', description: 'Sum of all current and non-current assets.' },
      { name: 'total_liabilities', description: 'Sum of all current and long-term liabilities.' },
      { name: 'equity', description: 'Total shareholders equity or net worth.' },
    ],
    useCases: [
      'Extract financial data from portfolio company reports for benchmarking',
      'Automate credit analysis by extracting key ratios from borrower financials',
      'Build structured datasets from annual reports for research and analytics',
      'Accelerate regulatory filing preparation with pre-extracted financial figures',
    ],
    relatedSolutionSlug: 'financial-services',
    relatedCaseStudySlugs: [],
    relatedBlogSlugs: [],
  },
  {
    slug: 'insurance-claims',
    name: 'Insurance Claim Extraction',
    plural: 'Insurance Claims',
    description:
      'Extract claim numbers, policy details, incident information, and adjuster notes from insurance claim documents.',
    heroDescription:
      'Insurance claim documents are dense, inconsistent, and time-sensitive. DocumentIQ extracts claim numbers, policy references, incident details, loss types, and claimed amounts from any claim form or adjuster report. Accelerate claims processing, reduce manual review cycles, and improve data quality for analytics and fraud detection.',
    fields: [
      { name: 'claim_number', description: 'Unique identifier assigned to the insurance claim.' },
      { name: 'policy_number', description: 'Policy number under which the claim is filed.' },
      { name: 'insured_name', description: 'Name of the policyholder or insured party.' },
      { name: 'incident_date', description: 'Date when the loss or incident occurred.' },
      { name: 'loss_type', description: 'Category of loss such as property damage, liability, or theft.' },
      { name: 'claimed_amount', description: 'Total amount claimed by the insured party.' },
      { name: 'adjuster_notes', description: 'Notes and observations recorded by the claims adjuster.' },
    ],
    useCases: [
      'Accelerate first notice of loss (FNOL) processing',
      'Extract structured data from adjuster reports for claims analytics',
      'Cross-reference claim details with policy terms for coverage validation',
      'Build datasets for fraud detection and pattern analysis',
    ],
    relatedSolutionSlug: 'financial-services',
    relatedCaseStudySlugs: [],
    relatedBlogSlugs: ['fnol-automation-ai-document-extraction-pc-claims-processing'],
  },
]

export const documentTypeSlugs = documentTypes.map((d) => d.slug)
